import { cssVar, keyframes } from '@chakra-ui/react'
import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/styled-system'
import { asChakraVariable } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

import { faxColorKeys } from './_colors'

const { FaxListItem } = faxColorKeys.Fax
const { Statuses: StatusColors, Thumbnail, Skeleton, PendingAnim } = FaxListItem

const FaxListItemParts = [
  'numberText',
  'directionIcon',
  'statusText',
  'thumbnailContainer',
  'thumbnailImage',
  'skeletonContainer',
  'skeleton',
  'thumbnailBox',
] as const

export enum FaxListItemVariant {
  Normal = 'Normal',
  Failed = 'Failed',
  Queued = 'Queued',
}

const helpers = createMultiStyleConfigHelpers(FaxListItemParts)

const $skeletonStartColor = cssVar(FaxListItem.Skeleton.StartColor)
const $skeletonEndColor = cssVar(FaxListItem.Skeleton.EndColor)

const pendingTextColorKeyframes = keyframes`
  from {
    color: ${asChakraVariable(PendingAnim.From)}
  }
  to {
    color: ${asChakraVariable(PendingAnim.To)}
  }
`

export const FaxListItemStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    numberText: {
      textStyle: textStyleKeys.Body1,
    },
    directionIcon: {
      width: '0.75rem',
      height: '0.75rem',
      color: StatusColors.Default,
    },
    statusText: {
      textStyle: textStyleKeys.Body3,
      color: StatusColors.Default,
    },
    thumbnailContainer: {
      flexDirection: 'column',
      alignItems: 'center',
      alignSelf: 'stretch',
      flexShrink: '0',
      position: 'relative',
      overflow: 'hidden',
    },
    thumbnailImage: {
      position: 'absolute',
      objectFit: 'contain',
      objectPosition: 'center',
    },
    skeletonContainer: {
      height: 'full',
      width: 'full',
    },
    skeleton: {
      [$skeletonStartColor.variable]: asChakraVariable(Skeleton.StartColor),
      [$skeletonEndColor.variable]: asChakraVariable(Skeleton.EndColor),
    },
    thumbnailBox: {
      border: '1px solid',
      borderColor: Thumbnail.Border,
      boxSizing: 'border-box',
      position: 'absolute',
    },
  },
  variants: {
    [FaxListItemVariant.Normal]: {},
    [FaxListItemVariant.Queued]: {
      statusText: {
        color: StatusColors.Queued,
        animation: `${pendingTextColorKeyframes} 1.5s ease-in-out infinite alternate`,
      },
      directionIcon: {
        color: StatusColors.Queued,
      },
    },
    [FaxListItemVariant.Failed]: {
      statusText: {
        color: StatusColors.Failed,
      },
      directionIcon: {
        color: StatusColors.Failed,
      },
    },
  },
  defaultProps: {
    variant: FaxListItemVariant.Normal,
  },
})

export const useFaxListItemStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof FaxListItemParts>(
    getName({ FaxListItemStyle }),
    props,
  )
}
